import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Key,
  ScrollText,
  RefreshCw,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  getAccessControlOverview,
  type AccessControlOverview,
} from "../../../service/securityService";

const AccessControlHub: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AccessControlOverview | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const o = await getAccessControlOverview();
      setData(o);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load access control overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cards = [
    {
      title: "Access zones",
      description: "Define premises, levels, and role rules for physical areas.",
      path: "/security/access-control/zones",
      icon: Shield,
    },
    {
      title: "Access assignments",
      description: "Grant users, visitors, and vendors time-bound access with approvals.",
      path: "/security/access-control/assignments",
      icon: Key,
    },
    {
      title: "Access logs",
      description: "View granted/denied events and record gate or reader events.",
      path: "/security/access-control/logs",
      icon: ScrollText,
    },
  ];

  return (
    <div className="px-2 py-4 w-full h-full mt-10 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Shield className="w-7 h-7 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Access Control</h1>
            <p className="text-gray-600 text-sm max-w-2xl">
              Zones, assignments, and access telemetry integrate with visitor
              management and cyber–physical monitoring. Hardware readers can POST
              access logs via the authenticated security API.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-20">
          <SpinLoader />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Zones</p>
              <p className="text-2xl font-bold text-gray-900">
                {data?.zones.total ?? "—"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {data?.zones.active ?? 0} active
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Assignments (in window)</p>
              <p className="text-2xl font-bold text-gray-900">
                {data?.assignments.activeNow ?? "—"}
              </p>
              <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {data?.assignments.pendingApproval ?? 0} pending approval
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Logs (7d) granted</p>
              <p className="text-2xl font-bold text-emerald-700">
                {data?.accessLogsLast7d.granted ?? "—"}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Logs (7d) denied</p>
              <p className="text-2xl font-bold text-red-600">
                {data?.accessLogsLast7d.denied ?? "—"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {data?.assignments.revokedLast7d ?? 0} revokes (7d)
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.path}
                  type="button"
                  onClick={() => navigate(c.path)}
                  className="text-left bg-white rounded-lg border border-gray-200 p-5 hover:border-[#0fa9a6] hover:shadow-sm transition group"
                >
                  <Icon className="w-8 h-8 text-[#0fa9a6] mb-3" />
                  <h2 className="font-semibold text-gray-900">{c.title}</h2>
                  <p className="text-sm text-gray-500 mt-1">{c.description}</p>
                  <span className="inline-flex items-center gap-1 mt-3 text-sm text-[#0fa9a6] font-medium group-hover:underline">
                    Open <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default AccessControlHub;
